import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Leaf, ArrowRight, CheckCircle, Globe, TrendingUp, Shield,
  Cloud, Recycle, Users, FileText, Target, Sparkles
} from 'lucide-react';

const SustainabilitySection = () => {
  const esgDimensions = [
    { name: 'Reporting & Governance', icon: FileText, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Climate & Emissions', icon: Cloud, color: 'from-teal-500 to-teal-600' },
    { name: 'Resource Efficiency', icon: Recycle, color: 'from-green-500 to-green-600' },
    { name: 'Biodiversity & Nature', icon: Leaf, color: 'from-lime-500 to-lime-600' },
    { name: 'Workforce & DEI', icon: Users, color: 'from-emerald-600 to-teal-500' },
    { name: 'Supply Chain ESG', icon: Globe, color: 'from-teal-600 to-green-500' },
  ];

  const features = [
    '10 ESG dimensions',
    'Regional frameworks (GRI, BRSR, CSRD)',
    'Industry benchmarking',
    '12-month roadmap',
    'ROI analysis',
    'C-suite ready insight report'
  ];

  const frameworks = ['GRI', 'ISSB', 'BRSR', 'CSRD/ESRS', 'TCFD'];

  return (
    <section className="py-20 bg-gradient-to-b from-emerald-950 via-teal-950 to-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-emerald-500/5 to-teal-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-6">
            <Leaf className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-300">NEW: Sustainability/ESG Assessment</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Sustainability Maturity Assessment
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Comprehensive ESG assessment aligned with global frameworks. Evaluate your organization&apos;s 
            sustainability maturity across 10 key dimensions with industry-specific benchmarks.
          </p>
          
          {/* Frameworks badges */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {frameworks.map((fw, idx) => (
              <span key={idx} className="text-xs bg-emerald-900/50 text-emerald-300 border border-emerald-700/50 px-3 py-1 rounded-full">
                {fw}
              </span>
            ))}
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - ESG Dimensions */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              10 ESG Dimensions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {esgDimensions.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={idx}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-white/10 hover:border-emerald-500/30 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-medium text-white text-sm">{item.name}</h4>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Additional info */}
            <div className="mt-6 p-4 bg-emerald-900/30 border border-emerald-700/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-emerald-300">URL Analysis Feature</span>
              </div>
              <p className="text-xs text-gray-400">
                Provide your company&apos;s sustainability report URL and our AI will analyze it to pre-fill and enhance your assessment.
              </p>
            </div>
          </div>

          {/* Right side - Features & CTA */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-400" />
              What You&apos;ll Get
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Report Preview */}
            <div className="bg-black/20 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">C-suite Ready Insight Report</p>
                  <p className="text-sm text-white">Executive summary, areas of concern, strategic roadmap</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full">Executive Summary</span>
                <span className="text-xs bg-teal-500/20 text-teal-300 px-2 py-1 rounded-full">Industry Comparison</span>
                <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full">ROI Analysis</span>
              </div>
            </div>

            {/* CTA Button */}
            <Link 
              to="/sustainability"
              className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-green-500 hover:from-emerald-600 hover:via-teal-600 hover:to-green-600 text-white font-semibold py-4 px-6 rounded-xl transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
              data-testid="launch-sustainability"
            >
              <Leaf className="w-5 h-5" />
              <span>Start ESG Assessment</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Takes ~10-15 minutes • AI-powered analysis • PowerPoint export
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '10', label: 'ESG Dimensions' },
            { value: '5', label: 'Maturity Levels' },
            { value: '30+', label: 'Industries Covered' },
            { value: '6', label: 'Regional Frameworks' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 via-teal-400 to-green-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SustainabilitySection;
